import { ExpandLess, ExpandMore } from '@mui/icons-material'
import { Alert, Collapse, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'
import { isEmpty, toUpper } from 'lodash'
import { Fragment, memo, useState } from 'react'

import { MicroLearningDetailExperiment } from '../../../api/models'
import { useOpenExperiment } from '../../../hooks/useOpenExperiment'

import styles from '../MicroLearningDetail.module.css'

type ExperimentsTableProps = {
  items: MicroLearningDetailExperiment[]
  showStatus: boolean
}

const WARNING_STATUSES = new Set(['missing_files', 'insufficient_content'])

const formatMetadataValue = (value?: string | null): string => {
  if (!value) return '—'
  return value
}

type ConfidencePart = { kind: 'proven' | 'confidence'; value: string }

const getConfidenceLevelParts = (
  hypothesisProven?: string | null,
  confidence?: string | null,
): ConfidencePart[] => {
  const proven = hypothesisProven === 'Yes' ? 'Proven' : hypothesisProven === 'No' ? 'Disproven' : null
  const parts: ConfidencePart[] = []
  if (proven) parts.push({ kind: 'proven', value: proven })
  if (confidence) parts.push({ kind: 'confidence', value: confidence })
  return parts
}

type ConfidenceCellProps = {
  hypothesisProven?: string | null
  confidence?: string | null
}

const ConfidenceCell = ({ hypothesisProven, confidence }: ConfidenceCellProps) => {
  const parts = getConfidenceLevelParts(hypothesisProven, confidence)
  if (parts.length === 0) return <>—</>
  return (
    <div className={styles.confidenceBadges}>
      {parts.map(part => (
        <span
          key={part.kind}
          className={styles.statusBadge}>
          {part.value}
        </span>
      ))}
    </div>
  )
}

const ExperimentsTable = ({ items, showStatus }: ExperimentsTableProps) => {
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({})
  const { openExperiment } = useOpenExperiment()

  const toggleRow = (id: string) => {
    setExpandedRows(previous => ({
      ...previous,
      [id]: !previous[id],
    }))
  }

  if (isEmpty(items)) {
    return <p className={styles.emptyText}>No experiments available.</p>
  }

  return (
    <div
      className={`${styles.tableWrap} ${!showStatus ? styles.tableNoStatus : ''} ${items.length > 5 ? styles.tableScrollable : ''}`}>
      <TableContainer className={styles.tableContainer}>
        <Table className={styles.tableElement}>
          <TableHead className={styles.tableHeader}>
            <TableRow className={styles.tableHeaderRow}>
              <TableCell className={`${styles.tableHeaderCell} ${styles.codeColumn}`}>Code</TableCell>
              <TableCell className={`${styles.tableHeaderCell} ${styles.countryColumn}`}>Country</TableCell>
              <TableCell className={`${styles.tableHeaderCell} ${styles.ouColumn}`}>OU</TableCell>
              <TableCell className={`${styles.tableHeaderCell} ${styles.periodColumn}`}>Period</TableCell>
              <TableCell className={`${styles.tableHeaderCell} ${styles.glaColumn}`}>GLA Strategic Priority</TableCell>
              {showStatus && (
                <TableCell className={`${styles.tableHeaderCell} ${styles.statusColumn}`}>Phase</TableCell>
              )}
              <TableCell className={`${styles.tableHeaderCell} ${styles.confidenceColumn}`}>Confidence Level</TableCell>
              <TableCell className={`${styles.tableHeaderCell} ${styles.actionColumn}`}>Action</TableCell>
              <TableCell className={`${styles.tableHeaderCell} ${styles.expandColumn}`}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map(exp => {
              const hasWarning =
                (exp.missing_criteria && exp.missing_criteria.length > 0) || WARNING_STATUSES.has(exp.status ?? '')
              const isExpanded = Boolean(expandedRows[exp.id])

              return (
                <Fragment key={exp.id}>
                  <TableRow
                    key={exp.id}
                    className={styles.tableRowMain}>
                    <TableCell className={`${styles.tableCell} ${styles.codeColumn}`}>
                      <span className={styles.experimentCode}>{exp.code}</span>
                    </TableCell>
                    <TableCell className={`${styles.tableCell} ${styles.countryColumn}`}>
                      <span className={styles.neutralPill}>{toUpper(exp.metadata?.country ?? '') || '—'}</span>
                    </TableCell>
                    <TableCell className={`${styles.tableCell} ${styles.ouColumn}`}>
                      {exp.metadata?.ou ?? '—'}
                    </TableCell>
                    <TableCell className={`${styles.tableCell} ${styles.periodColumn}`}>
                      {exp.metadata?.period || '—'}
                    </TableCell>
                    <TableCell className={`${styles.tableCell} ${styles.glaColumn}`}>
                      {exp.metadata?.gla_strategic_priority || '—'}
                    </TableCell>
                    {showStatus && (
                      <TableCell className={`${styles.tableCell} ${styles.statusColumn}`}>
                        <span className={styles.statusBadge}>{exp.status ?? '—'}</span>
                      </TableCell>
                    )}
                    <TableCell className={`${styles.tableCell} ${styles.confidenceColumn}`}>
                      <ConfidenceCell
                        hypothesisProven={exp.metadata?.hypothesis_proven}
                        confidence={exp.metadata?.confidence_in_results}
                      />
                    </TableCell>
                    <TableCell className={`${styles.tableCell} ${styles.actionColumn}`}>
                      <div className={styles.rowAction}>
                        {hasWarning && <span className={styles.warningIcon}>!</span>}
                        <button
                          type="button"
                          className={styles.detailsButton}
                          onClick={() => openExperiment(exp.id, true)}>
                          Details
                        </button>
                      </div>
                    </TableCell>
                    <TableCell className={`${styles.tableCell} ${styles.expandColumn}`}>
                      <button
                        type="button"
                        className={styles.expandButton}
                        onClick={() => toggleRow(exp.id)}
                        aria-label={isExpanded ? 'Collapse row details' : 'Expand row details'}>
                        {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                      </button>
                    </TableCell>
                  </TableRow>
                  <TableRow
                    key={`${exp.id}-details`}
                    className={styles.detailsRow}>
                    <TableCell
                      className={styles.detailsCell}
                      colSpan={showStatus ? 9 : 8}>
                      <Collapse
                        in={isExpanded}
                        timeout="auto"
                        unmountOnExit>
                        <div className={styles.rowDetails}>
                          {hasWarning && (
                            <Alert
                              severity="warning"
                              className={styles.warningBanner}>
                              This experiment is <b>missing critical data</b>. Please validate file uploads.
                            </Alert>
                          )}
                          <h4 className={styles.metadataTitle}>Metadata</h4>
                          <div className={styles.metadataDisposition}>
                            <div className={styles.metadataMetricsGrid}>
                              <div className={styles.metadataItem}>
                                <p className={styles.metadataLabel}>KPI</p>
                                <p className={styles.metadataValue}>{exp.metadata?.kpi || '—'}</p>
                              </div>
                              <div className={styles.metadataItem}>
                                <p className={styles.metadataLabel}>Impressions</p>
                                <p className={styles.metadataValueStrong}>
                                  {formatMetadataValue(exp.metadata?.impressions)}
                                </p>
                              </div>
                              <div className={styles.metadataItem}>
                                <p className={styles.metadataLabel}>Spend</p>
                                <p className={styles.metadataValue}>{formatMetadataValue(exp.metadata?.spend)}</p>
                              </div>
                              <div className={styles.metadataItem}>
                                <p className={styles.metadataLabel}>CPM</p>
                                <p className={styles.metadataValue}>{formatMetadataValue(exp.metadata?.cpm)}</p>
                              </div>
                            </div>
                            <div
                              className={styles.metadataContextColumn}
                              aria-label="Brand and global charter">
                              <div className={styles.metadataItem}>
                                <p className={styles.metadataLabel}>Brand</p>
                                <p className={styles.metadataValue}>{formatMetadataValue(exp.metadata?.brand)}</p>
                              </div>
                              <div className={styles.metadataItem}>
                                <p className={styles.metadataLabel}>Global Charter</p>
                                <p className={styles.metadataValue}>
                                  {formatMetadataValue(exp.metadata?.global_charter)}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </Fragment>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  )
}

export default memo(ExperimentsTable)
